import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { SectionCard, EmptyState } from "@/components/common/ui";
import { seriesColor } from "@/lib/chart";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Search, ChevronRight, Home, ReceiptText, ArrowLeft, Layers, ArrowDownWideNarrow,
  ArrowDownAZ, ListTree, Eye, Zap, Wallet,
} from "lucide-react";

const LEVELS = ["bucket", "category", "platform", "vendor"];
const LEVEL_LABEL = { bucket: "Cost Bucket", category: "Category", platform: "Platform", vendor: "Vendor", lineitems: "Line items" };
const LEVEL_PLURAL = { bucket: "Cost Buckets", category: "Categories", platform: "Platforms", vendor: "Vendors" };
const pctOf = (v, t) => (t ? Math.round((v / t) * 100) : 0);
const slug = (s) => String(s).replace(/[^a-zA-Z0-9]+/g, "-");

/**
 * SpendsExplorerUnified (PART 3 · 3.10) — merges the old "Investment Bucket" and
 * "Spends Explorer" into ONE component.
 *
 *  • Top: a compact KPI strip (Total spend · Invoices · Visibility · Performance)
 *    plus a by-vertical bar — the investment lens.
 *  • Below: a hierarchical drill-down (Cost Bucket → Category → Platform → Vendor
 *    → invoice lines) with search — the analyst lens.
 *
 * No Portfolio/Brand ("Selected/Brand") scope toggle — spend follows the global
 * project selection only. Visibility / Performance cards open the lines behind them.
 */
export function SpendsExplorerUnified({ period = "FY", projects = [] }) {
  const { money } = useCurrency();
  const projKey = (projects || []).join(",");

  // ---- investment KPI strip ----
  const [mix, setMix] = useState(null);
  const [mixLoading, setMixLoading] = useState(true);
  const [drill, setDrill] = useState(null); // vis/perf lines dialog

  // ---- drill-down explorer ----
  const [path, setPath] = useState([]);
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [grandTotal, setGrandTotal] = useState(0);
  const [sortMode, setSortMode] = useState("spend");

  const curLevel = path.length < LEVELS.length ? LEVELS[path.length] : "lineitems";
  const filters = useMemo(() => Object.fromEntries(path.map((c) => [c.level, c.name])), [path]);
  const filterKey = JSON.stringify(filters);

  // KPI strip
  useEffect(() => {
    let on = true; setMixLoading(true);
    const params = { period, scope: "portfolio" };
    if (projKey) params.projects = projKey;
    api.investmentMix(params)
      .then((d) => { if (on) { setMix(d); setMixLoading(false); } })
      .catch(() => on && setMixLoading(false));
    return () => { on = false; };
  }, [period, projKey]);

  const openLines = ({ vis_perf, title }) => {
    setDrill({ title, vis_perf, loading: true, res: null });
    const params = { period, scope: "portfolio" };
    if (vis_perf) params.vis_perf = vis_perf;
    if (projKey) params.projects = projKey;
    api.investmentMixLines(params)
      .then((res) => setDrill((d) => (d ? { ...d, loading: false, res } : d)))
      .catch(() => setDrill((d) => (d ? { ...d, loading: false, res: { items: [], total: 0, count: 0 } } : d)));
  };

  // reset drill on scope change
  useEffect(() => { setPath([]); }, [period, projKey]);

  useEffect(() => {
    let on = true; setLoading(true);
    const wantLines = path.length >= LEVELS.length || path.some((c) => c.jumpToLines);
    const params = { period, level: wantLines ? "lineitems" : curLevel, ...filters };
    if (projKey) params.projects = projKey;
    api.spendExplorer(params)
      .then((d) => {
        if (!on) return;
        if (wantLines) setView({ kind: "lines", items: d.items || [], total: d.total || 0, count: d.count ?? (d.items || []).length });
        else setView({ kind: "rows", rows: d.rows || [], total: d.total || 0 });
        if (path.length === 0) setGrandTotal(d.total || 0);
        setLoading(false);
      })
      .catch(() => on && setLoading(false));
    return () => { on = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, projKey, filterKey, curLevel]);

  // search -> flat line items
  useEffect(() => {
    const q = search.trim();
    if (!q) { setResults(null); setSearching(false); return; }
    setSearching(true);
    const params = { period, level: "lineitems", search: q };
    if (projKey) params.projects = projKey;
    const t = setTimeout(() => {
      api.spendExplorer(params)
        .then((d) => setResults(d.items || []))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(t);
  }, [search, period, projKey]);

  const drillIn = (name) => setPath((p) => [...p, { level: curLevel, name }]);
  const jumpToLines = (name) => setPath((p) => [...p, { level: curLevel, name, jumpToLines: true }]);
  const popTo = (idx) => setPath((p) => p.slice(0, idx));

  const levelTotal = view?.total || 0;
  const nextLevel = LEVELS[path.length + 1];

  const rows = useMemo(() => {
    if (view?.kind !== "rows") return [];
    const arr = [...(view.rows || [])];
    if (sortMode === "name") arr.sort((a, b) => String(a.name).localeCompare(String(b.name)));
    else arr.sort((a, b) => (b.spend || 0) - (a.spend || 0));
    return arr;
  }, [view, sortMode]);
  const maxSpend = rows.length ? Math.max(...rows.map((r) => r.spend || 0)) : 0;

  const vp = mix?.vis_perf || { visibility: 0, performance: 0 };
  const vpTotal = vp.visibility + vp.performance;

  return (
    <SectionCard
      className="border-l-[3px] border-l-[hsl(var(--chart-cat-6))]"
      title={(
        <span className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[hsl(var(--chart-cat-6)/0.15)] text-[hsl(var(--chart-cat-6))]">
            <ListTree className="h-4 w-4" />
          </span>
          <span className="flex flex-col leading-tight">
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[hsl(var(--chart-cat-6))]">Investment & drill-down</span>
            <span className="flex items-center gap-2">
              Spends Explorer
              <span className="rounded-full border border-[hsl(var(--chart-cat-6)/0.35)] bg-[hsl(var(--chart-cat-6)/0.1)] px-2 py-0.5 text-[10px] font-medium text-[hsl(var(--chart-cat-6))]">Unified</span>
            </span>
          </span>
        </span>
      )}
      description="Investment mix + full spend taxonomy in one place — Cost Bucket → Category → Platform → Vendor → invoice line. Search any spend and open the invoices behind it."
      testId="module-spends-explorer-unified"
      action={(
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search any spend…" className="h-8 w-48 pl-8" data-testid="explorer-search" />
        </div>
      )}>

      {/* ---- Investment KPI strip ---- */}
      {mixLoading && !mix ? (
        <div className="grid grid-cols-2 gap-3 border-b p-4 sm:grid-cols-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-lg" />)}</div>
      ) : (
        <div className="grid grid-cols-2 gap-3 border-b p-4 sm:grid-cols-4" data-testid="explorer-kpis">
          <div className="rounded-lg border bg-card p-3">
            <p className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground"><Wallet className="h-3.5 w-3.5 text-[hsl(var(--chart-cat-6))]" /> Total spend</p>
            <p className="num mt-1 font-heading text-xl font-semibold tabular-nums" data-testid="explorer-kpi-total">{money(mix?.total || 0)}</p>
          </div>
          <div className="rounded-lg border bg-card p-3">
            <p className="text-[11px] font-medium text-muted-foreground">Invoices</p>
            <p className="num mt-1 font-heading text-xl font-semibold tabular-nums" data-testid="explorer-kpi-invoices">{mix?.lines || 0}</p>
          </div>
          <button type="button" onClick={() => openLines({ vis_perf: "Visibility", title: "Visibility spend" })}
            className="group rounded-lg border bg-card p-3 text-left transition-colors duration-150 hover:border-[hsl(var(--chart-cat-1)/0.5)] hover:bg-[hsl(var(--surface-2))]"
            data-testid="explorer-kpi-visibility">
            <p className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground"><Eye className="h-3.5 w-3.5 text-[hsl(var(--chart-cat-1))]" /> Visibility</p>
            <p className="num mt-1 font-heading text-lg font-semibold tabular-nums">{money(vp.visibility)}</p>
            <p className="text-[10px] text-muted-foreground">{pctOf(vp.visibility, vpTotal)}% · <span className="text-[hsl(var(--primary))] group-hover:underline">view lines</span></p>
          </button>
          <button type="button" onClick={() => openLines({ vis_perf: "Performance", title: "Performance spend" })}
            className="group rounded-lg border bg-card p-3 text-left transition-colors duration-150 hover:border-[hsl(var(--chart-cat-3)/0.5)] hover:bg-[hsl(var(--surface-2))]"
            data-testid="explorer-kpi-performance">
            <p className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground"><Zap className="h-3.5 w-3.5 text-[hsl(var(--chart-cat-3))]" /> Performance</p>
            <p className="num mt-1 font-heading text-lg font-semibold tabular-nums">{money(vp.performance)}</p>
            <p className="text-[10px] text-muted-foreground">{pctOf(vp.performance, vpTotal)}% · <span className="text-[hsl(var(--primary))] group-hover:underline">view lines</span></p>
          </button>
        </div>
      )}

      {/* ---- breadcrumb trail ---- */}
      <div className="flex flex-wrap items-center gap-1.5 border-b px-4 py-2.5" data-testid="explorer-breadcrumb">
        <button type="button" onClick={() => popTo(0)}
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors duration-150 ${path.length === 0 ? "border-primary bg-[hsl(var(--primary)/0.1)] font-medium text-primary" : "hover:bg-[hsl(var(--surface-2))]"}`}
          data-testid="explorer-crumb-root">
          <Home className="h-3 w-3" /> All spend
        </button>
        {path.map((c, i) => (
          <React.Fragment key={`${c.level}-${c.name}`}>
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
            <button type="button" onClick={() => popTo(i + 1)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors duration-150 ${i === path.length - 1 ? "border-primary bg-[hsl(var(--primary)/0.1)] font-medium text-primary" : "hover:bg-[hsl(var(--surface-2))]"}`}
              data-testid={`explorer-crumb-${i}`}>
              <span className="text-[9px] uppercase tracking-wide text-muted-foreground">{LEVEL_LABEL[c.level]}</span>
              {c.name}
            </button>
          </React.Fragment>
        ))}
        <span className="ml-auto flex items-center gap-2">
          {path.length > 0 && (
            <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs" onClick={() => popTo(path.length - 1)} data-testid="explorer-back">
              <ArrowLeft className="h-3.5 w-3.5" /> Back
            </Button>
          )}
          <span className="num text-xs font-semibold tabular-nums" data-testid="explorer-total">{money(levelTotal)}</span>
        </span>
      </div>

      <div className="p-3">
        {search.trim() ? (
          searching && !results ? (
            <div className="space-y-2" data-testid="explorer-loading">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-9 rounded-md" />)}</div>
          ) : (results || []).length === 0 ? (
            <EmptyState title="No matches" message={`Nothing matches "${search}".`} />
          ) : (
            <ScrollArea className="max-h-[460px]">
              <table className="w-full border-collapse" data-testid="explorer-search-results">
                <thead><tr>
                  {["Month", "Activity", "Vendor", "Platform", "Category", "Amount"].map((h, i) => (
                    <th key={h} className={`th-sticky px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground ${h === "Amount" ? "text-right" : "text-left"} ${i === 0 ? "th-sticky-left" : ""}`}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {(results || []).map((r, i) => (
                    <tr key={r.id || i} className={`border-b last:border-0 ${i % 2 ? "row-zebra" : ""}`} data-testid={`explorer-result-${i}`}>
                      <td className="td-sticky-left px-3 py-2 text-xs text-muted-foreground">{r.month}</td>
                      <td className="px-3 py-2 text-sm font-medium">{r.activity}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{r.vendor}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{r.platform}</td>
                      <td className="px-3 py-2 text-xs">{r.vertical}</td>
                      <td className="px-3 py-2 num text-right text-sm font-semibold tabular-nums">{money(r.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollArea>
          )
        ) : loading && !view ? (
          <div className="space-y-2" data-testid="explorer-loading">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-lg" />)}</div>
        ) : view?.kind === "lines" ? (
          (view.items || []).length === 0 ? (
            <EmptyState title="No line items" message="No spend lines here." />
          ) : (
            <>
              <div className="mb-2.5 flex items-center gap-2 px-1 text-[11px] text-muted-foreground">
                <Badge variant="outline" className="gap-1 text-[10px]"><ReceiptText className="h-3 w-3" /> {view.count ?? view.items.length} line items</Badge>
                <span>Individual invoice lines for this selection.</span>
              </div>
              <ScrollArea className="max-h-[480px]">
                <table className="w-full border-collapse" data-testid="explorer-lineitems">
                  <thead><tr>
                    {["Month", "Activity", "Vertical", "Vendor", "Amount"].map((h, i) => (
                      <th key={h} className={`th-sticky px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground ${h === "Amount" ? "text-right" : "text-left"} ${i === 0 ? "th-sticky-left" : ""}`}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {view.items.map((r, i) => (
                      <tr key={r.id || i} className={`border-b last:border-0 ${i % 2 ? "row-zebra" : ""}`} data-testid={`explorer-lineitem-${i}`}>
                        <td className="td-sticky-left px-3 py-2 text-xs text-muted-foreground">{r.month}</td>
                        <td className="px-3 py-2 text-sm font-medium">{r.activity}</td>
                        <td className="px-3 py-2 text-xs">{r.vertical}</td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">{r.vendor}</td>
                        <td className="px-3 py-2 num text-right text-sm font-semibold tabular-nums">{money(r.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
            </>
          )
        ) : rows.length === 0 ? (
          <EmptyState title="No spend" message="No spend for this scope." />
        ) : (
          <div className="max-w-3xl" data-testid="explorer-rows-wrap">
            <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2 px-1">
              <Badge variant="outline" className="gap-1 text-[10px]">
                <Layers className="h-3 w-3" /> {rows.length} {LEVEL_PLURAL[curLevel]}
              </Badge>
              <div className="flex items-center gap-2">
                <span className="hidden text-[11px] text-muted-foreground md:inline">
                  {nextLevel ? `Tap a row to open its ${LEVEL_PLURAL[nextLevel]}` : "Tap a row to see its line items"}
                </span>
                <ToggleGroup type="single" value={sortMode} onValueChange={(v) => v && setSortMode(v)}
                  className="rounded-lg border bg-[hsl(var(--surface-2))] p-0.5" data-testid="explorer-sort-toggle">
                  <ToggleGroupItem value="spend" className="h-6 gap-1 px-2 text-[11px] data-[state=on]:bg-card data-[state=on]:shadow-sm" data-testid="explorer-sort-spend">
                    <ArrowDownWideNarrow className="h-3 w-3" /> Size
                  </ToggleGroupItem>
                  <ToggleGroupItem value="name" className="h-6 gap-1 px-2 text-[11px] data-[state=on]:bg-card data-[state=on]:shadow-sm" data-testid="explorer-sort-name">
                    <ArrowDownAZ className="h-3 w-3" /> A–Z
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>
            </div>

            <ScrollArea className="max-h-[420px] pr-1">
              <div className="space-y-1" data-testid="explorer-bar-list">
                {rows.map((r, i) => {
                  const share = pctOf(r.spend, levelTotal);
                  const barPct = maxSpend ? Math.max(3, Math.round((r.spend / maxSpend) * 100)) : 0;
                  const color = seriesColor(i % 8);
                  return (
                    <div key={r.name} role="button" tabIndex={0}
                      onClick={() => drillIn(r.name)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); drillIn(r.name); } }}
                      className="group relative flex cursor-pointer items-center gap-3 overflow-hidden rounded-md border bg-card px-3 py-2 outline-none transition-colors duration-150 hover:border-[hsl(var(--primary)/0.4)] focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary)/0.3)]"
                      data-testid={`explorer-row-${slug(r.name)}`}>
                      {/* proportional data-bar background */}
                      <span aria-hidden className="absolute inset-y-0 left-0 transition-[width] duration-300" style={{ width: `${barPct}%`, background: `${color}1f` }} />
                      <span className="relative z-10 w-4 shrink-0 text-right text-[11px] font-semibold tabular-nums text-muted-foreground">{i + 1}</span>
                      <span className="relative z-10 h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: color }} />
                      <span className="relative z-10 min-w-0 basis-52 shrink truncate text-sm font-medium" title={r.name}>{r.name}</span>
                      <span className="relative z-10 w-10 shrink-0 text-right text-[11px] tabular-nums text-muted-foreground">{share}%</span>
                      {r.count != null && (
                        <span className="relative z-10 hidden w-16 shrink-0 text-right text-[11px] tabular-nums text-muted-foreground sm:inline">{r.count} lines</span>
                      )}
                      <span className="relative z-10 num w-24 shrink-0 text-right text-sm font-semibold tabular-nums">{money(r.spend)}</span>
                      <span className="relative z-10 flex shrink-0 items-center gap-0.5">
                        <Button variant="ghost" size="sm"
                          className="h-7 gap-1 px-2 text-[11px] text-muted-foreground opacity-0 transition-opacity duration-150 group-hover:opacity-100 focus:opacity-100"
                          onClick={(e) => { e.stopPropagation(); jumpToLines(r.name); }}
                          data-testid={`explorer-lines-${slug(r.name)}`}>
                          <ReceiptText className="h-3.5 w-3.5" /> Lines
                        </Button>
                        <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform duration-150 group-hover:translate-x-0.5" />
                      </span>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
        )}
      </div>

      {/* vis/perf lines dialog */}
      <Dialog open={!!drill} onOpenChange={(o) => !o && setDrill(null)}>
        <DialogContent className="max-w-4xl" data-testid="explorer-visperf-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {drill?.vis_perf === "Visibility" ? <Eye className="h-4 w-4 text-[hsl(var(--chart-cat-1))]" /> : <Zap className="h-4 w-4 text-[hsl(var(--chart-cat-3))]" />}
              {drill?.title || "Spend"} — all invoices
            </DialogTitle>
            <DialogDescription>
              {drill?.loading ? "Loading\u2026" : `${drill?.res?.count || 0} invoices \u00b7 ${money(drill?.res?.total || 0)} total`}
            </DialogDescription>
          </DialogHeader>
          {drill?.loading ? (
            <div className="space-y-2 py-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-9 rounded-md" />)}</div>
          ) : (drill?.res?.items || []).length === 0 ? (
            <EmptyState title="No invoices" message={`No lines found for ${drill?.title || "this selection"}.`} />
          ) : (
            <ScrollArea className="max-h-[60vh]">
              <table className="w-full border-collapse" data-testid="explorer-visperf-table">
                <thead><tr>
                  {["Month", "Activity", "Vendor", "Project", "Vertical", "Platform", "Amount"].map((h, i) => (
                    <th key={h} className={`th-sticky px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground ${h === "Amount" ? "text-right" : "text-left"} ${i === 0 ? "th-sticky-left" : ""}`}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {(drill?.res?.items || []).map((r, i) => (
                    <tr key={r.id || i} className={`border-b last:border-0 ${i % 2 ? "row-zebra" : ""}`} data-testid={`explorer-visperf-line-${i}`}>
                      <td className="td-sticky-left px-3 py-2 text-xs text-muted-foreground">{r.month}</td>
                      <td className="px-3 py-2 text-sm font-medium">{r.activity}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{r.vendor}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{r.project}</td>
                      <td className="px-3 py-2 text-xs">{r.vertical}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{r.platform}</td>
                      <td className="px-3 py-2 num text-right text-sm font-semibold tabular-nums">{money(r.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
}
